import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Input, Popconfirm, Segmented, Space, Table, Tooltip, Typography, message,
} from 'antd';
import {
  DeleteOutlined, EditOutlined, EyeOutlined, FilterOutlined, HomeOutlined, PlayCircleOutlined,
  PlusOutlined, SafetyCertificateOutlined, SearchOutlined, SunOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { makeEligibilityRuleSetsApi } from '../services/eligibilityRuleSetsApi';
import EligibilityRuleSetEditor from '../components/AhriDashboard/EligibilityRuleSetEditor';
import { encodeHashRoute } from '../../../scripts/widgetHashRouter.js';
import '../styles/eligibility-rule-sets.css';

// Opens the AHRI system widget (v3, the current generation) in a new tab,
// scoped to the raw Solr fq clauses resolved from the selected rule sets.
// The widget reads these via a small hash-route extension added in
// ahri_v6/src/domain/routing.js (rawfq_indoor / rawfq_outdoor / rawfq_furnace)
// and ANDs them into every search for the life of that tab -- see
// AhriV6Widget.jsx's buildSearchFilters.
function openWidgetPreview({ indoorFq, outdoorFq, furnaceFq, ruleSetNames }) {
  const params = new URLSearchParams();
  if (indoorFq) params.set('rawfq_indoor', indoorFq);
  if (outdoorFq) params.set('rawfq_outdoor', outdoorFq);
  if (furnaceFq) params.set('rawfq_furnace', furnaceFq);
  (ruleSetNames || []).forEach((name) => params.append('rsname', name));
  const hash = encodeHashRoute({ segments: [], page: 'shop', params });
  window.open(`/ahri/shop/v3${hash}`, '_blank', 'noopener');
}

const UNIT_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'indoor', label: (
    <span><HomeOutlined style={{ marginRight: 6 }} />Indoor</span>
  ) },
  { value: 'outdoor', label: (
    <span><SunOutlined style={{ marginRight: 6 }} />Outdoor</span>
  ) },
  { value: 'furnace', label: (
    <span><ThunderboltOutlined style={{ marginRight: 6 }} />Furnace</span>
  ) },
];

const UNIT_TYPE_LABELS = { indoor: 'Indoor', outdoor: 'Outdoor', furnace: 'Furnace' };

const AhriEligibilityRuleSets = () => {
  const { getAuthHeaders } = useAuth();
  const api = useMemo(() => makeEligibilityRuleSetsApi(API_BASE_URL, getAuthHeaders), [getAuthHeaders]);

  const [ruleSets, setRuleSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRuleSet, setEditingRuleSet] = useState(null);
  const [simResults, setSimResults] = useState({});
  const [simulating, setSimulating] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [unitFilter, setUnitFilter] = useState('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [previewing, setPreviewing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.listRuleSets();
      setRuleSets(data.items || []);
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editingRuleSet) {
        await api.updateRuleSet(editingRuleSet.id, values);
        message.success('Rule set updated');
      } else {
        await api.createRuleSet(values);
        message.success('Rule set created');
      }
      setEditorOpen(false);
      setEditingRuleSet(null);
      await reload();
    } catch (err) {
      message.error(String(err?.message || err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleSet) => {
    try {
      await api.deleteRuleSet(ruleSet.id);
      message.success('Rule set deleted');
      await reload();
    } catch (err) {
      message.error(String(err?.message || err));
    }
  };

  const handleSimulate = async (ruleSet) => {
    setSimulating(ruleSet.id);
    try {
      const data = await api.simulateRuleSet(ruleSet.id);
      setSimResults((prev) => ({ ...prev, [ruleSet.id]: data.matched_variants }));
    } catch (err) {
      message.error(String(err?.message || err));
    } finally {
      setSimulating(null);
    }
  };

  const handlePreviewSelected = async () => {
    if (!selectedRowKeys.length) return;
    setPreviewing(true);
    try {
      const { indoor_fq: indoorFq, outdoor_fq: outdoorFq, furnace_fq: furnaceFq } = await api.previewRuleSetsFq(selectedRowKeys);
      if (!indoorFq && !outdoorFq && !furnaceFq) {
        message.warning('Selected rule set(s) resolved to no conditions -- nothing to scope by.');
        return;
      }
      const ruleSetNames = ruleSets
        .filter((rs) => selectedRowKeys.includes(rs.id))
        .map((rs) => rs.name);
      openWidgetPreview({ indoorFq, outdoorFq, furnaceFq, ruleSetNames });
    } catch (err) {
      message.error(String(err?.message || err));
    } finally {
      setPreviewing(false);
    }
  };

  const filteredRuleSets = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return ruleSets.filter((rs) => {
      // A multi-slot rule set surfaces when filtering to any one of its
      // applicable slots.
      const matchesUnitFilter = unitFilter === 'all' || (rs.unit_types || []).includes(unitFilter);
      if (!matchesUnitFilter) return false;
      if (q && !rs.name.toLowerCase().includes(q) && !(rs.predicate_description || '').toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [ruleSets, searchText, unitFilter]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name) => <span className="eligibility-rule-name">{name}</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Applies to',
      dataIndex: 'unit_types',
      width: 190,
      render: (unitTypes) => (
        <Space size={4} wrap>
          {(unitTypes || []).map((v) => (
            <span key={v} className={`unit-tag unit-tag--${v}`}>{UNIT_TYPE_LABELS[v] || v}</span>
          ))}
        </Space>
      ),
    },
    {
      title: 'Conditions',
      dataIndex: 'predicate_description',
      render: (desc) => (
        <Tooltip title={desc} placement="topLeft">
          <span className="eligibility-conditions-chip">{desc}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Assigned',
      dataIndex: 'assigned_customer_count',
      width: 110,
      align: 'center',
      sorter: (a, b) => a.assigned_customer_count - b.assigned_customer_count,
      render: (count) => (
        <span className={`count-pill ${count === 0 ? 'count-pill--zero' : ''}`}>{count}</span>
      ),
    },
    {
      title: 'Simulate',
      key: 'simulate',
      width: 190,
      render: (_, record) => (
        <Button
          size="small"
          icon={<PlayCircleOutlined />}
          loading={simulating === record.id}
          onClick={() => handleSimulate(record)}
        >
          {simResults[record.id] !== undefined ? `${simResults[record.id]} matches` : 'Simulate'}
        </Button>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingRuleSet(record);
                setEditorOpen(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this rule set?"
            description={
              record.assigned_customer_count > 0
                ? `This will unassign it from ${record.assigned_customer_count} customer(s).`
                : undefined
            }
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="eligibility-page">
      <div className="eligibility-page__header">
        <div>
          <Typography.Title level={3} className="eligibility-page__title">
            <SafetyCertificateOutlined style={{ marginRight: 10, color: 'var(--elig-primary)' }} />
            Customer Eligibility Rule Sets
          </Typography.Title>
          <div className="eligibility-page__subtitle">
            Build named, reusable eligibility conditions once with the query builder, then assign them to any number
            of customers from the <a href="/ahri/customer-eligibility">Customer Eligibility</a> page. A customer with
            no assigned rule sets (and no legacy rules) is fully unrestricted.
          </div>
        </div>
        <div className="eligibility-page__actions">
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingRuleSet(null);
              setEditorOpen(true);
            }}
          >
            New rule set
          </Button>
        </div>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} closable onClose={() => setError('')} />}

      <div className="eligibility-card">
        <div className="eligibility-card__toolbar">
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: 'var(--elig-text-muted)' }} />}
            placeholder="Search by name or condition..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <Segmented value={unitFilter} onChange={setUnitFilter} options={UNIT_FILTER_OPTIONS} />
          <div style={{ flex: 1 }} />
          {selectedRowKeys.length > 0 && (
            <Space>
              <Typography.Text type="secondary">{selectedRowKeys.length} selected</Typography.Text>
              <Button
                icon={<EyeOutlined />}
                loading={previewing}
                onClick={handlePreviewSelected}
              >
                Preview selected
              </Button>
            </Space>
          )}
        </div>
        <div className="eligibility-card__body">
          {filteredRuleSets.length === 0 && !loading ? (
            <div className="eligibility-empty-state">
              <FilterOutlined style={{ fontSize: 28, marginBottom: 10, opacity: 0.5 }} />
              <div>
                {ruleSets.length === 0
                  ? 'No rule sets yet -- click "New rule set" to build your first filter.'
                  : 'No rule sets match your search/filter.'}
              </div>
            </div>
          ) : (
            <Table
              className="eligibility-table"
              rowKey="id"
              loading={loading}
              dataSource={filteredRuleSets}
              columns={columns}
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
                preserveSelectedRowKeys: true,
              }}
              pagination={filteredRuleSets.length > 10 ? { pageSize: 10 } : false}
              scroll={{ x: 820 }}
            />
          )}
        </div>
      </div>

      <EligibilityRuleSetEditor
        open={editorOpen}
        initial={editingRuleSet}
        api={api}
        saving={saving}
        onCancel={() => {
          setEditorOpen(false);
          setEditingRuleSet(null);
        }}
        onSave={handleSave}
      />
    </div>
  );
};

export default AhriEligibilityRuleSets;
